export const createAsyncDataCache = ({ ttl = 30000, now = () => Date.now() } = {}) => {
  const cache = new Map();
  const pendingRequests = new Map();
  const generations = new Map();

  const nextGeneration = (key) => {
    const generation = (generations.get(key) || 0) + 1;
    generations.set(key, generation);
    return generation;
  };

  const peek = (key) => {
    const entry = cache.get(key);
    if (!entry) return null;
    return { data: entry.data, isFresh: now() - entry.updatedAt < ttl };
  };

  const load = (key, loader, { force = false } = {}) => {
    const cached = peek(key);
    if (!force && cached?.isFresh) return Promise.resolve(cached.data);

    const currentGeneration = generations.get(key) || 0;
    const pending = pendingRequests.get(key);
    if (!force && pending?.generation === currentGeneration) return pending.request;

    const generation = nextGeneration(key);
    const request = Promise.resolve(loader())
      .then((data) => {
        if (generations.get(key) === generation) {
          cache.set(key, { data, updatedAt: now() });
        }
        return data;
      })
      .finally(() => {
        if (pendingRequests.get(key)?.generation === generation) {
          pendingRequests.delete(key);
        }
      });

    pendingRequests.set(key, { generation, request });
    return request;
  };

  const invalidatePrefix = (prefix) => {
    const keys = new Set([
      ...cache.keys(),
      ...pendingRequests.keys(),
      ...generations.keys(),
    ]);
    for (const key of keys) {
      if (!key.startsWith(prefix)) continue;
      cache.delete(key);
      nextGeneration(key);
    }
  };

  return { invalidatePrefix, load, peek };
};
